import type { ReactNode } from "react";
import { DropdownMenu } from "radix-ui";
import { Check } from "lucide-react";

import { cn } from "@/lib/cn";

/**
 * A menu, in the one style every menu in the application has.
 *
 * A white panel with a soft shadow, rows the height of a sidebar row, a grey
 * ground under the pointer. The account menu, the New button, a filter, a
 * list's grouping and its columns are all this, so somebody who has opened
 * one has opened all of them.
 */
export const MENU_CONTENT =
  "z-50 min-w-48 max-w-80 rounded-md bg-surface-raised p-1 shadow-pop ring-1 ring-line " +
  "max-h-[min(24rem,var(--radix-dropdown-menu-content-available-height))] overflow-y-auto";

export const MENU_ITEM =
  "flex cursor-pointer select-none items-center gap-2.5 rounded-xs px-2.5 py-1.5 text-base text-fg " +
  "outline-none data-[highlighted]:bg-ink-100 data-[disabled]:cursor-not-allowed data-[disabled]:text-fg-placeholder";

export const MENU_LABEL = "px-2.5 pb-1 pt-1.5 text-2xs font-semibold text-fg-subtle";

export const MENU_SEPARATOR = "mx-1 my-1 h-px bg-line-faint";

export interface MenuProps {
  /** The button that opens it. One element that takes a ref and props. */
  trigger: ReactNode;
  align?: "start" | "center" | "end";
  children: ReactNode;
}

export function Menu({ trigger, align = "start", children }: MenuProps) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align={align} sideOffset={6} className={MENU_CONTENT}>
          {children}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return <DropdownMenu.Label className={MENU_LABEL}>{children}</DropdownMenu.Label>;
}

export function MenuSeparator() {
  return <DropdownMenu.Separator className={MENU_SEPARATOR} />;
}

export function MenuItem({
  icon,
  hint,
  onSelect,
  disabled,
  children,
}: {
  icon?: ReactNode;
  /** Quiet text at the trailing edge: a shortcut, a count. */
  hint?: ReactNode;
  onSelect: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <DropdownMenu.Item onSelect={onSelect} disabled={disabled} className={MENU_ITEM}>
      {icon ? (
        <span className="shrink-0 text-fg-subtle" aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {hint ? <span className="shrink-0 text-2xs text-fg-subtle">{hint}</span> : null}
    </DropdownMenu.Item>
  );
}

/** One of several, of which exactly one is chosen: a tick marks it. */
export function MenuChoice({
  selected,
  onSelect,
  children,
}: {
  selected: boolean;
  onSelect: () => void;
  children: ReactNode;
}) {
  return (
    <DropdownMenu.Item
      onSelect={onSelect}
      role="menuitemradio"
      aria-checked={selected}
      className={cn(MENU_ITEM, selected && "font-medium")}
    >
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {selected ? <Check className="size-4 shrink-0 text-accent" aria-hidden="true" /> : null}
    </DropdownMenu.Item>
  );
}

/** One of several, any number of which may be on. Stays open when toggled. */
export function MenuToggle({
  checked,
  onChange,
  children,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  children: ReactNode;
}) {
  return (
    <DropdownMenu.CheckboxItem
      checked={checked}
      onCheckedChange={onChange}
      onSelect={(event) => event.preventDefault()}
      className={MENU_ITEM}
    >
      <span
        className={cn(
          "grid size-4 shrink-0 place-items-center rounded-[4px] ring-1 ring-inset",
          checked ? "bg-primary text-primary-fg ring-primary" : "bg-surface ring-line-strong",
        )}
        aria-hidden="true"
      >
        {checked ? <Check className="size-3" strokeWidth={3} /> : null}
      </span>
      <span className="min-w-0 flex-1 truncate">{children}</span>
    </DropdownMenu.CheckboxItem>
  );
}
